import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import { IconButton, makeStyles, Tooltip } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import LocalOfferIcon from '@material-ui/icons/LocalOffer'
import AdminPage from '../../../admin/shared/AdminPage'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import { useGetAiSkills } from '../../../../../api/denaliAssistant'
import { formatRelativeTime } from '../../Analysis/SmartEndpoints/formatRelativeTime'
import SkillTagManagementModal from './SkillTagManagementModal'

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px var(--padding-md)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  skillCount: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  searchInput: {
    padding: '5px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    outline: 'none',
    width: '200px',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': { color: 'var(--color-neutral-400)' }
  },
  tableWrap: {
    '& .operational-table > thead > tr > th': {
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      padding: '10px 18px',
      borderBottom: '1px solid var(--color-neutral-200)'
    },
    '& .operational-table > thead': { backgroundColor: 'var(--color-neutral-050)' },
    '& .operational-table > tbody > tr > td': { padding: '10px 18px' }
  },
  mono: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)'
  },
  versionBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-600)'
  },
  liveBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  notLiveBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 500,
    fontStyle: 'italic',
    backgroundColor: 'var(--color-neutral-050)',
    color: 'var(--color-neutral-400)'
  },
  actionBtn: {
    color: 'var(--color-neutral-300)',
    transition: 'color 0.15s ease',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  },
  timestampText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)'
  },
  newBtnWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '6px 0'
  }
}))

function SkillsHome ({ root }) {
  const classes = useStyles()
  const history = useHistory()
  const [searchText, setSearchText] = useState('')
  const [tagModalSkill, setTagModalSkill] = useState(null)

  const { data: skillsData, isLoading } = useGetAiSkills({ includeAll: true })

  const tableData = useMemo(() => {
    if (!skillsData?.skills) return []
    return skillsData.skills.map(s => ({
      ...s,
      lastModified: s.modifiedDate || s.createdDate || null,
      lastModifiedBy: s.modifiedBy || s.createdBy || null
    }))
  }, [skillsData])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    {
      id: 'skillId',
      accessor: 'skillId',
      Header: 'ID',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    { id: 'skillName', accessor: 'skillName', Header: 'Name' },
    {
      id: 'version',
      accessor: 'version',
      Header: 'Latest',
      Cell: ({ value }) => <span className={classes.versionBadge}>v{value}</span>
    },
    {
      id: 'live',
      Header: 'Live',
      accessor: 'liveVersion',
      Cell: ({ row }) => {
        const lv = row.original.liveVersion
        if (lv != null) return <span className={classes.liveBadge}>v{lv}</span>
        return <span className={classes.notLiveBadge}>not live</span>
      }
    },
    {
      id: 'lastModified',
      accessor: 'lastModified',
      Header: 'Last Modified',
      Cell: ({ row }) => {
        const when = row.original.lastModified
        const who = row.original.lastModifiedBy
        if (!when) return <span className={classes.timestampText}>—</span>
        return (
          <Tooltip title={`${new Date(when).toLocaleString()}${who ? ` by ${who}` : ''}`} arrow>
            <span className={classes.timestampText}>
              {formatRelativeTime(when)}{who ? ` · ${who}` : ''}
            </span>
          </Tooltip>
        )
      }
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '2px' }}>
          <Tooltip title='Manage tags' arrow>
            <IconButton size='small' className={classes.actionBtn} onClick={() => setTagModalSkill(row.original.skillId)}>
              <LocalOfferIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Edit skill' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => history.push(`${root}/ai/skills/${row.original.skillId}`)}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ], [classes, history, root])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'skillId', desc: false }], [])

  return (
    <AdminPage>
      <div className={classes.newBtnWrap}>
        <Link to={`${root}/ai/skills/new`}>
          <SydButton size='sm' variant='primary' icon='add'>New Skill</SydButton>
        </Link>
      </div>

      {isLoading ? (
        <Loading />
      ) : tableData.length === 0 ? (
        <div style={{ padding: '48px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--text-size-sm)', color: 'var(--color-neutral-500)', marginBottom: '24px', lineHeight: 1.7 }}>
            No AI skills yet. Create one to get started.
          </div>
          <Link to={`${root}/ai/skills/new`}>
            <SydButton size='sm' variant='primary' icon='add'>New Skill</SydButton>
          </Link>
        </div>
      ) : (
        <>
          <div className={classes.toolbar}>
            <span className={classes.skillCount}>
              {tableData.length} {tableData.length === 1 ? 'skill' : 'skills'}
            </span>
            <input
              className={classes.searchInput}
              placeholder='Search skills…'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className={classes.tableWrap}>
            <OperationalTable
              variant='blank'
              itemName='AI Skills'
              columns={columns}
              data={tableData}
              defaultSort={defaultSort}
              searchText={searchText}
            />
          </div>
        </>
      )}

      {tagModalSkill && (
        <SkillTagManagementModal
          skillId={tagModalSkill}
          onClose={() => setTagModalSkill(null)}
        />
      )}
    </AdminPage>
  )
}

SkillsHome.propTypes = {
  root: PropTypes.string
}

export default SkillsHome
